import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, Loader } from 'freemium-ui';
import { AnimatePresence, motion } from 'framer-motion';
import { get, isEmpty, omit } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useGetSegments } from 'routes/screens/segments/hooks';
import Icon from 'shared/components/Icon';
import Spacer from 'shared/components/Spacer';
import { TEMPORARY_HIDDEN_FIELDS } from 'shared/constants';
import { useGetContactForm, useGetTransactionFields } from 'shared/hooks';
import { Center } from 'shared/styles';
import {
  AccountInfoType,
  SurveyQuestionType,
  SurveyType,
  TransactionFieldType,
} from 'shared/types';
import { useGetAllContactFields } from '../../../../hooks';
import {
  constructFilterFields,
  getRatingOptions,
  getRatingOptionsGroup,
} from './utils';
import {
  FieldType,
  ResponseFields,
  hiddenCustomFieldTypes,
  hiddenCustomFields,
} from './constants';
import { AppliedFilterType } from '../..';
import FilterComponent from './filterItem';
import { FilterForm, Flex } from './style';

type FilterType = {
  accountInfo?: AccountInfoType;
  setAppliedFilters: (filter: AppliedFilterType) => void;
  surveyInfo: SurveyType | undefined;
  applyFilter: (store: unknown | null) => void;
  handleReset: () => void;
  appliedFilters: any;
  listsData: any;
};

export type FieldsType = {
  id: string;
  name: string;
  type?: string;
  label?: string;
  required?: boolean;
  visible?: boolean;
};

const FilterContent = (props: FilterType) => {
  const { t } = useTranslation();
  const {
    setAppliedFilters,
    accountInfo,
    surveyInfo,
    applyFilter,
    appliedFilters,
    handleReset,
    listsData,
  } = props;

  const { contactList: lists = [] } = listsData || {};
  const { data: segmentsData = {} } = useGetSegments({
    refetchOnWindowFocus: true,
  });
  const { data: contactFields } = useGetAllContactFields();
  const { data: contactForm } = useGetContactForm();
  const { data: transactionFields } = useGetTransactionFields({
    surveyId: surveyInfo?.id,
    enabled: Boolean(surveyInfo?.id),
  });
  const { segment_rules: segments = [] } = segmentsData || {};

  const [contactFilterFields, setContactFilterFields] = useState<any>([]);
  const [transactionFilterFields, setTransactionFilterFields] = useState<any>(
    [],
  );
  const [isRestTriggered] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [showFilterApplied, setShowFilterApplied] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showSearchInput, setShowSearchInput] = useState<boolean>(false);
  const type = surveyInfo?.type;

  // syncing custom contact fields delete
  const checkDeletedContactFields = useCallback(
    (converted: any) => {
      const filteredContactFields = Object.keys(
        appliedFilters?.localState,
      ).filter((key: string) => key.includes('contact_response'));
      const removedContactFields = filteredContactFields.filter(
        (key: string) =>
          !converted.map((item: FieldsType & any) => item.key).includes(key),
      );
      if (removedContactFields.length) {
        applyFilter(omit(appliedFilters.localState, removedContactFields));
      }
    },
    [appliedFilters.localState, applyFilter],
  );

  // convert custom contact fields into filter JSON format
  const transformFields = useCallback(
    (contactFields: FieldsType[]) => {
      const filtered = contactFields.filter(
        (item: FieldsType) =>
          item?.type &&
          !hiddenCustomFieldTypes.includes(item?.type) &&
          !hiddenCustomFields.includes(item?.name) &&
          !TEMPORARY_HIDDEN_FIELDS.includes(item.name),
      );
      const converted = filtered.map((field: FieldsType & any) =>
        constructFilterFields({
          field,
          contactForm,
          prefix: 'contact_response',
        }),
      );
      setContactFilterFields(converted);
      checkDeletedContactFields(converted);
    },
    [checkDeletedContactFields, contactForm],
  );

  const { ratingOptions, ratingGroupOptions }: any = useMemo(() => {
    if (surveyInfo?.id) {
      const surveyQuestions = surveyInfo?.question_details?.questions;
      const defaultQuestion = surveyQuestions?.find(
        (q: SurveyQuestionType) => !!q.type_info?.pivot_question,
      );
      const ratingOptions = getRatingOptions(
        defaultQuestion?.type_info?.scale_properties?.range || [],
      );
      const ratingGroupOptions = getRatingOptionsGroup(
        defaultQuestion?.type_info?.scale_properties?.range,
      );
      return { ratingOptions, ratingGroupOptions };
    }
    return { ratingOptions: [], ratingGroupOptions: [] };
  }, [surveyInfo]);

  // to show (*) with filter header as filter applied
  useEffect(() => {
    setShowFilterApplied(
      !isEmpty(appliedFilters?.localState) ||
        appliedFilters?.filterParams?.length,
    );
  }, [appliedFilters]);

  useEffect(() => {
    if (contactFields && contactForm?.fields) {
      transformFields(contactFields);
    }
  }, [contactFields, contactForm, transformFields]);

  useEffect(() => {
    if (transactionFields?.fields?.length) {
      const filterableFields = transactionFields?.fields?.filter(
        (item: TransactionFieldType) => item.filterable && !item.deleted,
      );
      const converted = filterableFields?.map((field: TransactionFieldType) =>
        constructFilterFields({
          field,
          prefix: 'transaction_response',
        }),
      );
      setTransactionFilterFields(converted);
    } else {
      setTransactionFilterFields([]);
    }
  }, [transactionFields]);

  if (Object.keys(surveyInfo || {}).length !== 0 && !surveyInfo?.id) {
    return (
      <Center>
        <Loader />
      </Center>
    );
  }

  return (
    <FilterForm>
      <div className="filter-content">
        <header className="filter-header">
          <h4>
            {t('responses.filter_cp')} {showFilterApplied ? '*' : ''}{' '}
          </h4>
          <Button
            inline
            type="secondary"
            className="icon-button"
            onClick={(e) => {
              e.preventDefault();
              setShowSearchInput(!showSearchInput);
            }}
          >
            <Icon glyph="search" width={14} height={14} viewBox="0 0 16 16" />
          </Button>
        </header>
        <AnimatePresence>
          {showSearchInput ? (
            <motion.div
              key={'response-filter'}
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: -10 }}
              exit={{ opacity: 0, y: 0 }}
            >
              <Spacer y={1} />
              <Input
                autoFocus
                value={searchTerm}
                className="search-input"
                startAdornment={
                  <div className="search-icon">
                    <Icon
                      glyph="search"
                      width={14}
                      height={14}
                      viewBox="0 0 16 16"
                    />
                  </div>
                }
                endAdornment={
                  searchTerm.length ? (
                    <div
                      className="clear-icon"
                      onClick={() => setSearchTerm('')}
                    >
                      <Icon glyph="clear" width={10} height={10} />
                    </div>
                  ) : null
                }
                placeholder={`${t('forms.placeholders.search_fields')}`}
                onChange={(e: any) => setSearchTerm(e.target.value.trim())}
              />
            </motion.div>
          ) : null}
        </AnimatePresence>

        <Spacer y={1} />
        {ResponseFields.filter(
          (item: FieldType) =>
            item?.name?.search(new RegExp(searchTerm, 'i')) > -1,
        ).length ? (
          <>
            <h6>{t('responses.filter.response_fields')}</h6>
            <div className="divider" />
            <Spacer y={1} />
          </>
        ) : null}
        {ResponseFields.filter(
          (item) => item?.name?.search(new RegExp(searchTerm, 'i')) > -1,
        )
          .filter((item) => item.groupId === 'ALL' || item.groupId === type)
          .map((item: FieldType, index: number) => (
            <div key={`${item.id}-${index}`} className="fields">
              <FilterComponent
                item={item}
                fieldValue={get(appliedFilters?.localState, item.id)}
                isRestTriggered={isRestTriggered}
                contactLists={lists}
                segments={segments || []}
                saveFilter={setAppliedFilters}
                accountInfo={accountInfo}
                appliedFilters={appliedFilters}
                setIsDisabled={setIsDisabled}
                ratingOptions={ratingOptions}
                ratingGroupOptions={ratingGroupOptions}
                surveyInfo={surveyInfo}
                filterFor="RESPONSE"
              />
            </div>
          ))}

        <Spacer y={1} />
        {contactFilterFields.filter(
          (item: FieldType) =>
            item?.name?.search(new RegExp(searchTerm, 'i')) > -1,
        ).length ? (
          <>
            <h6>{t('responses.filter.contact_fields')}</h6>
            <div className="divider" />
            <Spacer y={1} />
          </>
        ) : null}
        {contactFilterFields
          .filter(
            (item: FieldType) =>
              item?.name?.search(new RegExp(searchTerm, 'i')) > -1,
          )
          .map((customField: FieldType, index: number) => (
            <div key={`${customField.id}-${index}`} className="fields">
              <FilterComponent
                item={customField}
                fieldValue={get(appliedFilters.localState, customField.id)}
                isRestTriggered={isRestTriggered}
                contactLists={lists}
                segments={segments || []}
                saveFilter={setAppliedFilters}
                accountInfo={accountInfo}
                appliedFilters={appliedFilters}
                setIsDisabled={setIsDisabled}
                ratingOptions={ratingOptions}
                ratingGroupOptions={ratingGroupOptions}
                surveyInfo={surveyInfo}
                filterFor="RESPONSE"
              />
            </div>
          ))}

        <Spacer y={1} />
        {transactionFilterFields.filter(
          (item: FieldType) =>
            item?.name?.search(new RegExp(searchTerm, 'i')) > -1,
        ).length ? (
          <>
            <h6>{t('responses.filter.transaction_fields')}</h6>
            <div className="divider" />
            <Spacer y={1} />
          </>
        ) : null}
        {transactionFilterFields
          .filter(
            (item: FieldType) =>
              item?.name?.search(new RegExp(searchTerm, 'i')) > -1 &&
              item.name !== 'survey_info_id',
          )
          .map((field: FieldType, index: number) => (
            <div key={`${field.id}-${index}`} className="fields">
              <FilterComponent
                item={field}
                fieldValue={get(appliedFilters.localState, field.id)}
                isRestTriggered={isRestTriggered}
                contactLists={lists}
                saveFilter={setAppliedFilters}
                accountInfo={accountInfo}
                appliedFilters={appliedFilters}
                setIsDisabled={setIsDisabled}
                surveyInfo={surveyInfo}
                filterFor="QUEUE"
              />
            </div>
          ))}
      </div>
      <div className="filter-footer">
        <Flex justifyContent="flex-end">
          <Button
            htmlType="button"
            type="secondary"
            inline
            onClick={() => handleReset()}
          >
            {t('clear')}
          </Button>
          <Button
            inline
            type="primary"
            disabled={isDisabled}
            onClick={() => applyFilter(null)}
          >
            {t('apply')}
          </Button>
        </Flex>
      </div>
    </FilterForm>
  );
};

export default FilterContent;
